import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatARS } from "@/lib/api";
import { useAuth } from "@/App";
import { subscribeCustomerToPush, isPushSupported } from "@/lib/push";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Instagram, Facebook, Globe, MapPin, Phone, Calendar as CalIcon, Bell } from "lucide-react";
import { format, addDays } from "date-fns";
import { es } from "date-fns/locale";

export default function PublicBooking() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  const [courts, setCourts] = useState([]);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [occupied, setOccupied] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [customer, setCustomer] = useState({ name: "", phone: "+549 ", notes: "" });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (user?.name && !customer.name) {
      setCustomer((c) => ({ ...c, name: user.name }));
    }
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        const [o, c] = await Promise.all([
          api.get(`/orgs/public/${slug}`),
          api.get(`/courts/public/${slug}`),
        ]);
        setOrg(o.data);
        setCourts(c.data);
        if (c.data.length > 0) setSelectedCourt(c.data[0]);
      } catch {
        toast.error("Club no encontrado");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  useEffect(() => {
    if (!selectedDate) return;
    (async () => {
      const { data } = await api.get(`/bookings/public/${slug}/occupied`, { params: { date: selectedDate } });
      setOccupied(data);
    })();
  }, [selectedDate, slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center font-heading text-2xl">Cargando...</div>;
  if (!org) return <div className="min-h-screen flex items-center justify-center text-zinc-400">Club no encontrado</div>;
  if (org.available === false) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6" data-testid="public-unavailable">
        <div className="text-center max-w-md">
          <div className="font-heading text-4xl sm:text-5xl tracking-tight mb-3">{org.name.toUpperCase()}</div>
          <div className="text-xl text-zinc-300 mb-2">Negocio temporalmente no disponible</div>
          <p className="text-zinc-500 text-sm">Las reservas online están pausadas por el momento. {org.phone && `Podés comunicarte al ${org.phone}.`}</p>
        </div>
      </div>
    );
  }

  const brand = org.brand_color || "#10B981";
  const hours = [];
  for (let h = org.open_hour ?? 8; h < (org.close_hour ?? 23); h++) {
    hours.push(`${String(h).padStart(2, "0")}:00`);
  }

  const isOccupied = (time) =>
    selectedCourt && occupied.some((o) => o.court_id === selectedCourt.court_id && o.start_time === time);

  const days = Array.from({ length: 14 }, (_, i) => addDays(new Date(), i));

  const submit = async () => {
    if (!selectedCourt || !selectedTime || !customer.name || !customer.phone) {
      return toast.error("Completá todos los campos");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/bookings/public/${slug}`, {
        court_id: selectedCourt.court_id,
        customer_name: customer.name,
        customer_phone: customer.phone,
        customer_email: user?.email || null,
        date: selectedDate,
        start_time: selectedTime,
        notes: customer.notes,
      });
      setSuccess(data);
      toast.success("Reserva enviada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return <SuccessScreen success={success} org={org} brand={brand} slug={slug} customerPhone={customer.phone} />;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${brand}20, transparent)` }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
          <div className="flex items-center gap-5 mb-6">
            {org.logo_url ? (
              <img src={org.logo_url} alt="" className="h-16 w-16 rounded-lg object-cover border" style={{ borderColor: brand }} />
            ) : (
              <div className="h-16 w-16 rounded-lg flex items-center justify-center font-heading text-3xl" style={{ background: `${brand}30`, color: brand, border: `1px solid ${brand}` }}>
                {org.name[0]}
              </div>
            )}
            <div>
              <div className="text-xs uppercase tracking-[0.3em] mb-1" style={{ color: brand }}>// Reservá online</div>
              <h1 className="font-heading text-4xl md:text-5xl tracking-tight" data-testid="public-org-name">{org.name.toUpperCase()}</h1>
            </div>
          </div>
          {org.description && <p className="text-zinc-400 max-w-2xl">{org.description}</p>}
          <div className="flex flex-wrap gap-4 mt-4 text-sm text-zinc-400">
            {org.phone && <span className="flex items-center gap-1"><Phone className="h-4 w-4" />{org.phone}</span>}
            {org.address && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{org.address}</span>}
            {org.social_instagram && <a href={org.social_instagram} target="_blank" rel="noreferrer" className="hover:text-white"><Instagram className="h-4 w-4" /></a>}
            {org.social_facebook && <a href={org.social_facebook} target="_blank" rel="noreferrer" className="hover:text-white"><Facebook className="h-4 w-4" /></a>}
            {org.social_website && <a href={org.social_website} target="_blank" rel="noreferrer" className="hover:text-white"><Globe className="h-4 w-4" /></a>}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-10 space-y-8">
        {courts.length === 0 ? (
          <div className="bg-[#141414] border border-[#262626] rounded-lg p-16 text-center text-zinc-500">
            Este club aún no publicó canchas.
          </div>
        ) : (
          <>
            {/* Courts selector */}
            <section>
              <div className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: brand }}>// 1. Elegí tu cancha</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {courts.map((c) => (
                  <button
                    key={c.court_id}
                    onClick={() => setSelectedCourt(c)}
                    className={`text-left p-5 rounded-lg border-2 transition ${selectedCourt?.court_id === c.court_id ? "" : "border-[#262626] hover:border-zinc-700"}`}
                    style={selectedCourt?.court_id === c.court_id ? { background: `${brand}15`, borderColor: brand } : { background: "#141414" }}
                    data-testid={`court-opt-${c.court_id}`}
                  >
                    <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">{c.sport === "futbol" ? "⚽ Fútbol" : "🎾 Pádel"}</div>
                    <div className="font-heading text-2xl">{c.name}</div>
                    <div className="mt-2 font-bold" style={{ color: brand }}>{formatARS(c.price_ars)}</div>
                  </button>
                ))}
              </div>
            </section>

            {/* Date selector */}
            <section>
              <div className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: brand }}>// 2. Elegí el día</div>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {days.map((d) => {
                  const dstr = format(d, "yyyy-MM-dd");
                  const active = selectedDate === dstr;
                  return (
                    <button
                      key={dstr}
                      onClick={() => { setSelectedDate(dstr); setSelectedTime(null); }}
                      className="px-4 py-3 rounded-lg border-2 min-w-[80px] transition"
                      style={active ? { background: `${brand}15`, borderColor: brand } : { background: "#141414", borderColor: "#262626" }}
                      data-testid={`day-${dstr}`}
                    >
                      <div className="text-xs uppercase tracking-widest text-zinc-500">{format(d, "EEE", { locale: es })}</div>
                      <div className="font-heading text-2xl">{format(d, "d")}</div>
                      <div className="text-xs text-zinc-500">{format(d, "MMM", { locale: es })}</div>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Time selector */}
            <section>
              <div className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: brand }}>// 3. Elegí el horario</div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {hours.map((h) => {
                  const busy = isOccupied(h);
                  const active = selectedTime === h;
                  return (
                    <button
                      key={h}
                      disabled={busy}
                      onClick={() => setSelectedTime(h)}
                      className="py-3 rounded border-2 font-heading text-lg tracking-wider transition disabled:opacity-30 disabled:cursor-not-allowed"
                      style={
                        active
                          ? { background: brand, borderColor: brand, color: "#000" }
                          : busy
                          ? { background: "#141414", borderColor: "#262626", color: "#71717a" }
                          : { background: "#141414", borderColor: "#262626" }
                      }
                      data-testid={`time-${h}`}
                    >
                      {h}
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Customer form */}
            <section className="bg-[#141414] border border-[#262626] rounded-lg p-6">
              <div className="text-xs uppercase tracking-[0.3em] mb-6" style={{ color: brand }}>// 4. Tus datos</div>
              <div className="space-y-4">
                <div>
                  <Label className="uppercase tracking-widest text-xs text-zinc-400">Nombre</Label>
                  <Input value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] mt-1" data-testid="pub-name" />
                </div>
                <div>
                  <Label className="uppercase tracking-widest text-xs text-zinc-400">WhatsApp</Label>
                  <Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="bg-[#0A0A0A] border-[#262626] mt-1" placeholder="+549 11 2345 6789" data-testid="pub-phone" />
                </div>
                <div>
                  <Label className="uppercase tracking-widest text-xs text-zinc-400">Nota (opcional)</Label>
                  <Input value={customer.notes} onChange={(e) => setCustomer({ ...customer, notes: e.target.value })} className="bg-[#0A0A0A] border-[#262626] mt-1" data-testid="pub-notes" />
                </div>
              </div>
            </section>

            <Button
              onClick={submit}
              disabled={submitting || !selectedTime || !customer.name || !customer.phone}
              className="w-full text-black uppercase tracking-widest font-bold h-14 text-base"
              style={{ background: brand }}
              data-testid="pub-submit"
            >
              {submitting ? "Enviando..." : `Reservar ${selectedCourt ? formatARS(selectedCourt.price_ars) : ""}`}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function SuccessScreen({ success, org, brand, slug, customerPhone }) {
  const [pushState, setPushState] = useState({ supported: false, subscribed: false, busy: false });
  const ticketUrl = `${window.location.origin}/turno/${success.public_code || success.booking_id}`;

  useEffect(() => {
    (async () => {
      const supported = await isPushSupported();
      setPushState((s) => ({ ...s, supported }));
    })();
  }, []);

  const enable = async () => {
    setPushState((s) => ({ ...s, busy: true }));
    try {
      await subscribeCustomerToPush(slug, customerPhone);
      setPushState((s) => ({ ...s, subscribed: true, busy: false }));
      toast.success("Te avisaremos cuando confirmen tu turno");
    } catch (e) {
      setPushState((s) => ({ ...s, busy: false }));
      toast.error(e.message || "No se pudo activar");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(ticketUrl);
      toast.success("Link copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#0A0A0A]" style={{ "--brand": brand }}>
      <div className="max-w-md w-full bg-[#141414] border border-[#262626] rounded-lg p-10 text-center" data-testid="booking-success">
        <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: `${brand}20`, border: `2px solid ${brand}` }}>
          <CalIcon className="h-8 w-8" style={{ color: brand }} />
        </div>
        <h2 className="font-heading text-3xl mb-3 tracking-wider">¡RESERVA ENVIADA!</h2>
        <p className="text-zinc-400 mb-6">{org.name} va a confirmar tu turno por WhatsApp.</p>
        <div className="bg-[#0A0A0A] rounded p-4 text-left text-sm space-y-1 mb-6">
          <div><span className="text-zinc-500">Cancha:</span> {success.court_name}</div>
          <div><span className="text-zinc-500">Fecha:</span> {success.date}</div>
          <div><span className="text-zinc-500">Hora:</span> {success.start_time}</div>
          <div><span className="text-zinc-500">Precio:</span> {formatARS(success.price_ars)}</div>
        </div>

        <div className="mb-4 p-4 rounded-lg border text-left" style={{ borderColor: `${brand}40`, background: `${brand}08` }} data-testid="ticket-link-box">
          <div className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: brand }}>
            🎟 Tu link de turno
          </div>
          <p className="text-xs text-zinc-400 mb-3">
            Guardalo. Ahí podés ver detalles y cancelar si no podés ir.
          </p>
          <div className="flex gap-2">
            <a
              href={`/turno/${success.public_code || success.booking_id}`}
              className="flex-1 bg-[#0A0A0A] border border-[#262626] rounded px-3 py-2 text-xs text-zinc-300 truncate hover:border-zinc-500"
              data-testid="ticket-link-anchor"
            >
              {ticketUrl}
            </a>
            <Button
              type="button"
              onClick={copyLink}
              variant="outline"
              className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-[#141414] text-xs uppercase tracking-widest"
              data-testid="ticket-link-copy"
            >
              Copiar
            </Button>
          </div>
        </div>

        {pushState.supported && !pushState.subscribed && (
          <div className="mb-4 p-4 rounded-lg border" style={{ borderColor: `${brand}40`, background: `${brand}10` }} data-testid="customer-push-prompt">
            <div className="text-xs uppercase tracking-widest font-bold mb-1" style={{ color: brand }}>
              <Bell className="h-3 w-3 inline mr-1" /> Enterate al instante
            </div>
            <p className="text-xs text-zinc-400 mb-3">
              Activá las notificaciones y te avisamos en este dispositivo cuando el club confirme.
            </p>
            <Button
              type="button"
              onClick={enable}
              disabled={pushState.busy}
              className="w-full text-black uppercase tracking-widest text-xs font-bold"
              style={{ background: brand }}
              data-testid="customer-push-enable-btn"
            >
              {pushState.busy ? "Activando..." : "Activar notificaciones"}
            </Button>
          </div>
        )}
        {pushState.subscribed && (
          <div className="mb-4 p-3 rounded text-xs flex items-center gap-2 justify-center" style={{ background: `${brand}15`, color: brand }} data-testid="customer-push-on">
            <Bell className="h-3 w-3" /> Notificaciones activadas
          </div>
        )}
        <Button onClick={() => window.location.reload()} className="w-full text-black uppercase tracking-widest font-bold" style={{ background: brand }} data-testid="new-booking-btn">
          Nueva reserva
        </Button>
      </div>
    </div>
  );
}
